import ConfirmModal from '@/components/confirm-modal'
import {
    cancelOrderLabel,
    purchaseOrderLabel,
    refreshOrderLabelTracking,
    type Order,
} from '@/lib/api/orders/order-api'
import { mask, unMask } from '@/lib/form-state/util'
import { useState } from 'react'
import { MdLocalPrintshop, MdOutlineSell, MdRefresh } from 'react-icons/md'
import { toast } from 'react-toastify'

const formatCurrency = (cents: number): string =>
    `R$ ${(cents / 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`

/** Situação da etiqueta no Melhor Envio, como a equipe lê. */
const LABEL_STATUS: Record<string, { label: string; className: string }> = {
    cart: { label: 'No carrinho, sem pagamento', className: 'badge-warning' },
    pending: { label: 'No carrinho, sem pagamento', className: 'badge-warning' },
    released: { label: 'Paga, aguardando geração', className: 'badge-info' },
    generated: { label: 'Pronta para imprimir', className: 'badge-info' },
    received: { label: 'Recebida no ponto de coleta', className: 'badge-info' },
    posted: { label: 'Postada', className: 'badge-success' },
    delivered: { label: 'Entregue', className: 'badge-success' },
    undelivered: { label: 'Entrega não realizada', className: 'badge-error' },
    paused: { label: 'Entrega pausada', className: 'badge-warning' },
    suspended: { label: 'Envio suspenso', className: 'badge-error' },
    canceled: { label: 'Cancelada', className: 'badge-ghost' },
}

/** CPF ou CNPJ com máscara, conforme a quantidade de dígitos. */
const maskDocument = (value: string) => {
    const digits = unMask(value)
    return digits.length > 11
        ? mask(digits.slice(0, 14), '99.999.999/9999-99')
        : mask(digits, '999.999.999-99')
}

interface Props {
    order: Order
    canEdit: boolean
    /** Recarrega o pedido: a entrega e a etiqueta vêm no mesmo GET. */
    onChanged?: () => void
}

/**
 * Etiqueta do Melhor Envio do pedido de loja: compra com o saldo da conta da loja, imprime
 * e acompanha o rastreio. A postagem marca o pedido como despachado e avisa o cliente.
 */
export default function OrderShippingLabel({ order, canEdit, onChanged }: Props) {
    const shipping = order.shipping
    const [recipientDocument, setRecipientDocument] = useState('')
    const [invoiceKey, setInvoiceKey] = useState('')
    const [confirmPurchase, setConfirmPurchase] = useState(false)
    const [confirmCancel, setConfirmCancel] = useState(false)
    const [busy, setBusy] = useState<'purchase' | 'tracking' | 'cancel' | null>(null)

    if (!shipping || shipping.kind === 'PICKUP') return null

    const status = shipping.labelStatus ?? null
    const hasActiveLabel = !!shipping.labelOrderId && status !== 'canceled'
    const incomplete = status === 'cart' || status === 'pending' || status === 'released'
    const posted = status === 'posted' || status === 'delivered'
    const paid = order.billing?.status === 'PAID'
    const clientHasDocument = !!unMask(order.client?.cpf ?? '')
    const estimatedPrice = shipping.labelPrice ?? shipping.carrierPrice ?? shipping.price

    const run = async (
        kind: 'purchase' | 'tracking' | 'cancel',
        action: () => Promise<{ success: boolean; error?: string }>,
        successMessage: string
    ) => {
        setBusy(kind)
        try {
            const response = await action()
            if (response.success) {
                toast.success(successMessage)
                onChanged?.()
            } else {
                toast.error(response.error || 'Não foi possível concluir a operação')
            }
        } finally {
            setBusy(null)
            setConfirmPurchase(false)
            setConfirmCancel(false)
        }
    }

    const purchase = () =>
        run(
            'purchase',
            () =>
                purchaseOrderLabel(order.id, {
                    recipientDocument: unMask(recipientDocument) || undefined,
                    invoiceKey: unMask(invoiceKey) || undefined,
                }) as any,
            'Etiqueta comprada'
        )

    return (
        <div className="border-t border-base-200 pt-5 mb-6">
            <div className="flex items-center justify-between gap-3 mb-3">
                <h4 className="font-semibold text-base-content flex items-center gap-2">
                    <MdOutlineSell className="text-lg" />
                    Etiqueta do Melhor Envio
                </h4>
                {status && LABEL_STATUS[status] && (
                    <span className={`badge badge-sm ${LABEL_STATUS[status].className}`}>
                        {LABEL_STATUS[status].label}
                    </span>
                )}
            </div>

            {hasActiveLabel ? (
                <div className="flex flex-col gap-3">
                    <p className="text-sm text-base-content/70">
                        {shipping.labelPrice != null && (
                            <>Valor da etiqueta: {formatCurrency(shipping.labelPrice)}. </>
                        )}
                        {posted
                            ? 'A transportadora já recebeu o pacote.'
                            : 'O rastreio entra sozinho quando o pacote for postado.'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {shipping.labelUrl && !incomplete && (
                            <a
                                href={shipping.labelUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-primary btn-sm gap-1"
                            >
                                <MdLocalPrintshop className="text-lg" />
                                Imprimir etiqueta
                            </a>
                        )}
                        {canEdit && incomplete && (
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                disabled={busy !== null}
                                onClick={purchase}
                            >
                                {busy === 'purchase' && (
                                    <span className="loading loading-spinner loading-xs" />
                                )}
                                Concluir compra
                            </button>
                        )}
                        {canEdit && (
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm gap-1"
                                disabled={busy !== null}
                                onClick={() =>
                                    run(
                                        'tracking',
                                        () => refreshOrderLabelTracking(order.id) as any,
                                        'Rastreio atualizado'
                                    )
                                }
                            >
                                {busy === 'tracking' ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    <MdRefresh className="text-lg" />
                                )}
                                Atualizar rastreio
                            </button>
                        )}
                        {canEdit && !posted && (
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm text-error"
                                disabled={busy !== null}
                                onClick={() => setConfirmCancel(true)}
                            >
                                Cancelar etiqueta
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-base-content/70">
                        Compra a etiqueta com o saldo da conta da loja no Melhor Envio
                        {estimatedPrice ? <> (cerca de {formatCurrency(estimatedPrice)})</> : null}.
                        O rastreio entra no pedido sozinho e o cliente é avisado quando o
                        pacote for postado.
                    </p>
                    {!paid && (
                        <p className="text-sm text-warning">
                            A cobrança ainda não foi paga. A etiqueta fica disponível depois do
                            pagamento.
                        </p>
                    )}
                    {canEdit && paid && (
                        <>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                {!clientHasDocument && (
                                    <div className="form-control">
                                        <label className="label pb-1">
                                            <span className="label-text font-medium">
                                                CPF do cliente
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            className="input input-bordered w-full"
                                            placeholder="000.000.000-00"
                                            value={recipientDocument}
                                            onChange={e =>
                                                setRecipientDocument(maskDocument(e.target.value))
                                            }
                                        />
                                        <label className="label pt-1">
                                            <span className="label-text-alt text-base-content/50">
                                                Exigido pelo Melhor Envio; o cadastro do cliente não tem.
                                            </span>
                                        </label>
                                    </div>
                                )}
                                <div className="form-control">
                                    <label className="label pb-1">
                                        <span className="label-text font-medium">
                                            Chave da NF-e (opcional)
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        className="input input-bordered w-full"
                                        placeholder="44 dígitos"
                                        value={invoiceKey}
                                        onChange={e => setInvoiceKey(unMask(e.target.value).slice(0, 44))}
                                    />
                                    <label className="label pt-1">
                                        <span className="label-text-alt text-base-content/50">
                                            Sem nota, o envio vai com declaração de conteúdo.
                                        </span>
                                    </label>
                                </div>
                            </div>
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={busy !== null}
                                    onClick={() => {
                                        if (!clientHasDocument && unMask(recipientDocument).length < 11) {
                                            toast.error('Informe o CPF do cliente')
                                            return
                                        }
                                        setConfirmPurchase(true)
                                    }}
                                >
                                    <MdOutlineSell className="text-lg" />
                                    Comprar etiqueta
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            <ConfirmModal
                isOpen={confirmPurchase}
                onClose={() => setConfirmPurchase(false)}
                onConfirm={purchase}
                loading={busy === 'purchase'}
                title="Comprar etiqueta"
                message={`O valor${estimatedPrice ? ` (cerca de ${formatCurrency(estimatedPrice)})` : ''} é debitado do saldo da conta da loja no Melhor Envio. Continuar?`}
                confirmButtonText="Comprar"
                confirmButtonClass="btn btn-primary !border-0 !rounded-lg"
            />
            <ConfirmModal
                isOpen={confirmCancel}
                onClose={() => setConfirmCancel(false)}
                onConfirm={() =>
                    run('cancel', () => cancelOrderLabel(order.id) as any, 'Etiqueta cancelada')
                }
                loading={busy === 'cancel'}
                title="Cancelar etiqueta"
                message="A etiqueta é cancelada no Melhor Envio e o valor volta ao saldo em até 12 horas, se o cancelamento for aprovado."
                confirmButtonText="Cancelar etiqueta"
                cancelButtonText="Voltar"
            />
        </div>
    )
}
